/**
 * CaptainPickDialog — mandatory captain selection before Finalize (Build 4).
 * Shown when the user hits "Finalize Team" with no captain set: they must
 * pick one (2× multiplier) before the finalize confirm appears.
 */
import { useEffect, useState } from 'react';
import { Crown, Check } from 'lucide-react';
import { Button } from './ui/button';
import EntityAvatar from './EntityAvatar';

export default function CaptainPickDialog({ open, slots = [], onConfirm, onCancel, busy = false }) {
  const [selectedId, setSelectedId] = useState(null);
  // 2026-08-28 (owner: easier finalize) — pre-select the priciest star as the
  // suggested captain so finalizing is one tap; still changeable.
  useEffect(() => {
    if (!open) { setSelectedId(null); return; }
    const best = [...slots].sort((a, b) =>
      ((b.entity?.pricing?.price ?? b.acquired_price ?? 0) -
       (a.entity?.pricing?.price ?? a.acquired_price ?? 0)))[0];
    setSelectedId(best?.entity_id ?? null);
  }, [open, slots]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="captain-pick-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative w-full max-w-sm bg-card border border-border rounded-2xl shadow-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Crown className="h-5 w-5 text-yellow-500" fill="currentColor" />
          <h3 className="text-base font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
            Choose your Captain
          </h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Before finalizing, pick a Captain — they earn a <strong>2× multiplier</strong> on
          every ₣ they generate. We've suggested your priciest star — tap another to change.
          You can change Captain once per gameweek.
        </p>
        <div className="max-h-64 overflow-y-auto space-y-1.5 mb-4 pr-1">
          {slots.map((slot) => {
            const entity = slot.entity || {};
            const selected = selectedId === slot.entity_id;
            return (
              <button
                key={slot.entity_id}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl border text-left transition-colors ${
                  selected
                    ? 'border-yellow-500/70 bg-yellow-400/10'
                    : 'border-border/60 bg-background hover:bg-secondary/40'
                }`}
                onClick={() => setSelectedId(slot.entity_id)}
                data-testid={`captain-pick-option-${slot.entity_id}`}
              >
                <EntityAvatar entity={entity} size={32} />
                <span className="flex-1 text-sm font-medium truncate">{entity.name || 'Unknown'}</span>
                {selected && <Check className="h-4 w-4 text-yellow-600 shrink-0" />}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={busy} data-testid="captain-pick-cancel">
            Back
          </Button>
          <Button
            className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold"
            disabled={!selectedId || busy}
            onClick={() => selectedId && onConfirm?.(selectedId)}
            data-testid="captain-pick-confirm"
          >
            <Crown className="h-4 w-4 mr-1.5" fill="currentColor" />
            {busy ? 'Setting…' : 'Set Captain & Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
